// <purpose>Self-explaining state card for a DELIBERATE reply-engine no-draft — renders in the
// KI-Vorschlag slot where the letter would be, so a designed empty suggestion is never
// mistaken for breakage (the leadership-batch "18 wrong" root cause). Shows zone, the
// German explanation for WHY no draft exists, per-request ask/reason chips, and override
// collisions („Außerdem erkannt").</purpose>
// <dependencies>replyEngineMeta (explanation/labels/zone meta from the labels-only envelope);
// useCategoryMeta + marktprozessTint for the header badge (mirrors KlaerfallEditor's header);
// theme tokens; ZoneChip from shared.</dependencies>
// <constraints>Reads ONLY the labels-only envelope (SuggestionInfo.replyEngine) + reviewReasons —
// never telemetry. Layout stability: everything renders from one fetch at mount; nothing
// appears, disappears, or resizes afterwards. Same card frame + min-height register as a
// rendered suggestion so switching tickets doesn't jump the panel.</constraints>
import React from 'react';
import { ClerkInstruction, ReplyEngineInfo } from '../types';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { marktprozessTint } from '../services/categoryMeta';
import { ZoneChip } from './shared';
import ClerkInstructionPanel from './ClerkInstructionPanel';
import { escalationReasonLabel, noDraftExplanation, overrideNoun } from '../services/replyEngineMeta';

interface NoDraftCardProps {
  replyEngine: ReplyEngineInfo;
  reviewReasons?: string[];
  marktprozess: string;
  eskalationsstufe?: number | null;
  // E3: generated instruction fills the explanation slot; null ⇒ the deterministic static copy
  // below renders instead (the generator's fail-open fallback).
  clerkInstruction?: ClerkInstruction | null;
}

// Ask labels are the engine's closed AskLabel vocabulary (not schema anliegen ids) — small
// German map, fail-open to the raw label so a new upstream label is visible, never hidden.
const ASK_LABELS: Record<string, string> = {
  allgemeine_fachfrage: 'Allgemeine Fachfrage',
  nicht_fachlich: 'Nicht fachlich',
};
const askLabel = (label: string): string => ASK_LABELS[label] ?? label;

const chipStyle = (tintText: string, tintFill: string, tintBorder: string): React.CSSProperties => ({
  fontSize: 10, fontWeight: 500,
  color: tintText, backgroundColor: tintFill, border: `1px solid ${tintBorder}`,
  borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap',
});

const NoDraftCard: React.FC<NoDraftCardProps> = ({ replyEngine, reviewReasons, marktprozess, eskalationsstufe, clerkInstruction }) => {
  const cm = useCategoryMeta();
  const tint = marktprozessTint(cm.color(marktprozess));
  const { headline, sentence } = noDraftExplanation(replyEngine, reviewReasons);

  const esk = Math.max(0, Math.min(3, eskalationsstufe ?? 0));
  const requests = replyEngine.requests ?? [];
  const labels = Array.from(new Set(requests.map(r => r.label).filter((l): l is string => !!l)));
  const reasons = Array.from(new Set(
    requests.flatMap(r => (r.issues ?? []).map(i => i.reason)).filter((r): r is string => !!r)
  ));
  const collisions = replyEngine.overrideCollisions ?? [];

  return (
    <div style={{
      fontFamily: theme.font.family, borderRadius: theme.borderRadius.md,
      boxShadow: theme.shadows.cardLifted, backgroundColor: theme.colors.white,
      border: `1px solid ${theme.colors.borderLight}`, overflow: 'hidden',
    }}>
      {/* Header — mirrors KlaerfallEditor's header row exactly (badge · Stufe · zone right) */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexWrap: 'wrap',
        padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
        borderBottom: `1px solid ${theme.colors.bgLight}`, backgroundColor: theme.colors.bgPage,
      }}>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 12,
          color: theme.colors.white, backgroundColor: cm.color(marktprozess),
        }}>{cm.label(marktprozess)}</span>
        {esk >= 1 && (() => {
          const e = theme.colors.eskalation[`s${esk}` as 's1' | 's2' | 's3'];
          return (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
              color: e.fg, backgroundColor: e.bg, border: `1px solid ${e.border}`,
            }}>Stufe {esk}</span>
          );
        })()}
        <ZoneChip zone={replyEngine.zone} style={{ marginLeft: 'auto' }} />
      </div>

      {/* Body — one suggestion-height canvas (~140px), all content present at mount.
          Generated instruction wins the explanation slot; static copy is the null-fallback. */}
      <div style={{ padding: theme.spacing.md, minHeight: 140, fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid }}>
        {clerkInstruction ? (
          <div style={{ marginBottom: theme.spacing.sm }}>
            <ClerkInstructionPanel instruction={clerkInstruction} />
          </div>
        ) : (
          <>
            <div style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>{headline}</div>
            <div style={{ marginBottom: theme.spacing.sm }}>{sentence}</div>
          </>
        )}
        {collisions.length > 0 && (
          <div style={{ fontSize: 11, color: theme.colors.navyMuted, marginBottom: theme.spacing.sm }}>
            Außerdem erkannt: {collisions.map(overrideNoun).join(', ')}
          </div>
        )}
        {(labels.length > 0 || reasons.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing.xs }}>
            {labels.map(l => (
              <span key={`l-${l}`} style={chipStyle(tint.text, tint.fill, tint.border)}>
                {cm.secondaryAxisNoun}: {askLabel(l)}
              </span>
            ))}
            {reasons.map(r => (
              <span key={`r-${r}`} title={r} style={chipStyle(theme.colors.navyMid, theme.colors.bgLight, theme.colors.borderLight)}>
                {escalationReasonLabel(r)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NoDraftCard;
